import React from "react";
import { Typography, Collapse, Row, Col, Image } from "antd";
import { CaretRightOutlined } from "@ant-design/icons";
import "./FAQs.css";

const { Title } = Typography;
const { Panel } = Collapse;

const FAQs: React.FC = () => {
  const customExpandIcon = ({ isActive }: { isActive?: boolean }) => (
    <CaretRightOutlined
      rotate={isActive ? 90 : 0}
      style={{
        fontSize: "16px",
        color: "#4979A7",
        transition: "transform 0.3s",
      }}
    />
  );
  const applicationFAQs = [
    {
      key: "app-1",
      question: "When will I receive my payment?",
      answer:
        "Payments are typically processed within 5-7 business days after approval. You will receive an email notification once your payment has been sent.",
    },
    {
      key: "app-2",
      question: "What if I did not receive my payment?",
      answer:
        "Please contact our support team if you haven't received your payment within 10 business days. Make sure to check your spam folder for payment notifications.",
    },
    {
      key: "app-3",
      question: "How long do sales stay in progress?",
      answer:
        "Sales typically remain in progress status for 3-5 business days while we verify the documentation and process the reimbursement request.",
    },
    {
      key: "app-4",
      question: "How do I track the status of my Reimbursement Request?",
      answer:
        "You can track your reimbursement request status through your dashboard. You'll also receive email notifications for status updates.",
    },
    {
      key: "app-5",
      question: "How do I receive a reimbursement payment?",
      answer:
        "Reimbursement payments are sent via your preferred payment method (ACH transfer or check) as specified in your shop profile.",
    },
    {
      key: "app-6",
      question:
        "What if I need to change my banking information or payment preference?",
      answer:
        "You can update your banking information and payment preferences in your shop settings. Changes will take effect for future payments.",
    },
  ];

  const incentiveCodeFAQs = [
    {
      key: "inc-1",
      question: "When will I receive my payment?",
      answer:
        "Incentive code reimbursements are processed within 7-10 business days after the sale is verified and approved.",
    },
    {
      key: "inc-2",
      question: "What if I did not receive my payment?",
      answer:
        "If you haven't received payment for a validated incentive code sale, please contact support with your transaction details.",
    },
    {
      key: "inc-3",
      question: "How long do sales stay in progress?",
      answer:
        "Incentive code sales remain in progress while we verify customer eligibility and documentation, typically 5-7 business days.",
    },
    {
      key: "inc-4",
      question:
        "What if a customer wants to return a bike purchased with an Incentive Code?",
      answer:
        "Returns must be processed according to your store's return policy. The incentive amount will need to be refunded to the program.",
    },
    {
      key: "inc-5",
      question: "Can I discount a bike on top of the incentive?",
      answer:
        "Yes, you can apply additional store discounts on top of the incentive amount. The incentive code discount is applied first.",
    },
    {
      key: "inc-6",
      question: "What are the different statuses for my Reimbursement Request?",
      answer:
        "Statuses include: Pending (under review), Approved (payment processing), Funded (payment sent), and Declined (needs attention).",
    },
  ];

  const bikeShopFAQs = [
    {
      key: "shop-1",
      question: "When will I receive my payment?",
      answer:
        "Shop reimbursement payments are processed bi-weekly and sent via your preferred payment method.",
    },
    {
      key: "shop-2",
      question: "What if I did not receive my payment?",
      answer:
        "Check your payment status in the dashboard. If there are issues, contact support with your reimbursement request details.",
    },
    {
      key: "shop-3",
      question: "How do I track the status of my Reimbursement Request?",
      answer:
        "Use the Shop Reimbursements page to view all your requests and their current status in real-time.",
    },
    {
      key: "shop-4",
      question: "What if I need to give another Bike Shop Employee access?",
      answer:
        "Admin users can add additional employees through the Users section. Contact support if you need admin privileges.",
    },
    {
      key: "shop-5",
      question: "What if I need to remove a Bike Shop Employee?",
      answer:
        "Admin users can deactivate employee access through the Users management section in your dashboard.",
    },
    {
      key: "shop-6",
      question:
        "What if I need to change my banking information or payment preference?",
      answer:
        "Update your payment information in your shop profile settings. Changes apply to future payments only.",
    },
  ];

  return (
    <div className="faqs-page">
      {/* Page Header */}
      <div className="faqs-header">
        <img src="/images/faqs-icon.svg" alt="FAQs" className="faqs-icon" />
        <Title level={2} className="faqs-title">
          Frequently Asked Questions
        </Title>
      </div>

      {/* FAQ Sections */}
      <div className="faqs-content">
        {/* Application Section */}
        <div className="faq-section-wrapper">
          <Row gutter={[20, 0]} align="top">
            <Col xs={24} md={10}>
              <div className="faq-image-container">
                <Image
                  src="/images/chart-application.webp"
                  alt="Application FAQ"
                  className="faq-image"
                  preview={false}
                />
              </div>
            </Col>
            <Col xs={24} md={14}>
              <div className="faq-section">
                <div className="faq-category-header">
                  <div className="faq-category-header-content">
                    <img
                      src="/images/app-icon.svg"
                      alt="Application"
                      className="faq-category-icon"
                    />
                    <span className="faq-category-title">Application</span>
                  </div>
                </div>
                <Collapse
                  expandIcon={customExpandIcon}
                  className="faq-collapse"
                  bordered={false}
                  ghost
                >
                  {applicationFAQs.map((faq) => (
                    <Panel
                      header={
                        <span className="faq-question">{faq.question}</span>
                      }
                      key={faq.key}
                      className="faq-panel"
                    >
                      <div className="faq-answer">{faq.answer}</div>
                    </Panel>
                  ))}
                </Collapse>
                <div className="faq-divider"></div>
              </div>
            </Col>
          </Row>
        </div>

        {/* Incentive Code Section */}
        <div className="faq-section-wrapper">
          <Row gutter={[20, 0]} align="top">
            <Col xs={24} md={14}>
              <div className="faq-section">
                <div className="faq-category-header">
                  <div className="faq-category-header-content">
                    <img
                      src="/images/code-icon.svg"
                      alt="Incentive Code"
                      className="faq-category-icon"
                    />
                    <span className="faq-category-title">Incentive Code</span>
                  </div>
                </div>
                <Collapse
                  expandIcon={customExpandIcon}
                  className="faq-collapse"
                  bordered={false}
                  ghost
                >
                  {incentiveCodeFAQs.map((faq) => (
                    <Panel
                      header={
                        <span className="faq-question">{faq.question}</span>
                      }
                      key={faq.key}
                      className="faq-panel"
                    >
                      <div className="faq-answer">{faq.answer}</div>
                    </Panel>
                  ))}
                </Collapse>
                <div className="faq-divider"></div>
              </div>
            </Col>
            <Col xs={24} md={10}>
              <div className="faq-image-container">
                <Image
                  src="/images/main-bike-image.webp"
                  alt="Incentive Code FAQ"
                  className="faq-image"
                  preview={false}
                 
                />
              </div>
            </Col>
          </Row>
        </div>

        {/* Bike Shop Experience Section */}
        <div className="faq-section-wrapper">
          <Row gutter={[20, 0]} align="top">
            <Col xs={24} md={10}>
              <div className="faq-image-container">
                <Image
                  src="/images/chart-shop.webp"
                  alt="Bike Shop Experience FAQ"
                  className="faq-image"
                  preview={false}
                />
              </div>
            </Col>
            <Col xs={24} md={14}>
              <div className="faq-section">
                <div className="faq-category-header">
                  <div className="faq-category-header-content">
                    <img
                      src="/images/shop-icon.svg"
                      alt="Bike Shop Experience"
                      className="faq-category-icon"
                    />
                    <span className="faq-category-title">
                      Bike Shop Experience
                    </span>
                  </div>
                </div>
                <Collapse
                  expandIcon={customExpandIcon}
                  className="faq-collapse"
                  bordered={false}
                  ghost
                >
                  {bikeShopFAQs.map((faq) => (
                    <Panel
                      header={
                        <span className="faq-question">{faq.question}</span>
                      }
                      key={faq.key}
                      className="faq-panel"
                    >
                      <div className="faq-answer">{faq.answer}</div>
                    </Panel>
                  ))}
                </Collapse>
                <div className="faq-divider"></div>
              </div>
            </Col>
          </Row>
        </div>
      </div>
    </div>
  );
};

export default FAQs;
